/**
 * Tile zoom at which server-side MVT clustering stops (exclusive).
 * Aligned with API `MappingSettings.StartZoomLevelForCluster` default (12).
 *
 * Below this zoom: `clusterServerSide=true` (lines/polygons → centerpoint Points).
 * At/above this zoom: `clusterServerSide=false` (real LineString/Polygon geometry).
 */
export const MVT_SERVER_CLUSTER_MAX_ZOOM = 12;

/**
 * Max camera zoom for org overview fit / zoom-to-extent.
 * Keeps the first paint (and zoom-to-extent) inside the server cluster band
 * (`clusterServerSide=true` for tile z below {@link MVT_SERVER_CLUSTER_MAX_ZOOM}).
 */
export const ORG_OVERVIEW_FIT_MAX_ZOOM = MVT_SERVER_CLUSTER_MAX_ZOOM - 1;

const MVT_TILE_ZOOM_PATH = /\/tiles\/(\d+)\/\d+\/\d+\.(?:mvt|pbf)(?:\?|$)/i;

/**
 * Whether a tile zoom should request server-side MVT clustering.
 *
 * @param tileZ - Tile zoom from the request path.
 * @param maxZoom - Exclusive max zoom for clustering. @default {@link MVT_SERVER_CLUSTER_MAX_ZOOM}
 */
export function shouldClusterMvtServerSide(
	tileZ: number,
	maxZoom: number = MVT_SERVER_CLUSTER_MAX_ZOOM,
): boolean {
	return tileZ < maxZoom;
}

/**
 * Reads the tile zoom `z` from an MVT/PBF tile URL path (`/tiles/{z}/{x}/{y}.mvt`).
 *
 * @param url - Absolute or relative tile URL (after Mapbox substitutes z/x/y).
 * @returns Parsed zoom, or `null` when the path is not a tile URL.
 */
export function parseMvtTileZoom(url: string): number | null {
	const match = MVT_TILE_ZOOM_PATH.exec(url);
	if (match == null) return null;
	const zoom = Number(match[1]);
	return Number.isFinite(zoom) ? zoom : null;
}

/**
 * Sets or replaces the `clusterServerSide` query param on a tile URL.
 *
 * @param url - Tile URL (may already include query params).
 * @param cluster - Whether server-side clustering should be enabled.
 */
export function setMvtClusterServerSideQuery(
	url: string,
	cluster: boolean,
): string {
	const hashIndex = url.indexOf("#");
	const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
	const hash = hashIndex >= 0 ? url.slice(hashIndex) : "";

	const qIndex = withoutHash.indexOf("?");
	const path = qIndex >= 0 ? withoutHash.slice(0, qIndex) : withoutHash;
	const search = qIndex >= 0 ? withoutHash.slice(qIndex + 1) : "";

	const params = new URLSearchParams(search);
	params.set("clusterServerSide", String(cluster));

	const query = params.toString();
	return query.length > 0 ? `${path}?${query}${hash}` : `${path}${hash}`;
}
